import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Voucher } from 'utils/Voucher';
import { IconButton, IconLoader } from 'components/common/Icon';
import produce from 'immer';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import { TextField } from 'components/common/TextField';
import ReactLoading from 'react-loading';
import SearchField from 'components/common/SearchField';
import { parseSearchTerms, searchWithinVouchers } from 'utils/search';
import { Button } from 'components/common/Button';
import formatPrice from 'utils/format-price';
import FilterDropdown from 'components/common/FilterDropdown';
import { alertCircleIcon, checkCircleIcon, editIcon, eyeIcon, eyeOffIcon, trashIcon } from 'assets/icons';
import { requests } from 'api-services';

const voucherFields: (keyof Voucher)[] = [
  'code',
  'discountType',
  'discountValue',
  'isValid',
];

const ColumnHeader = ({ name }: { name: string }) => {
  return <th
    scope="col"
    className='px-2 py-1 md:px-6 md:py-3 text-xs md:text-s text-center text-gray-500 uppercase tracking-wider'
  >
    {name}
  </th>;
};

const VoucherFieldCell = ({ voucher, field }: { voucher: Voucher, field: keyof Voucher }) => {
  if (field === 'isValid') {
    return <input type='checkbox' disabled checked={voucher[field]} />;
  } else if (field === 'discountValue') {
    if (voucher.discountType === 'Fixed') {
      return <>{formatPrice(voucher[field])}</>;
    }
    return <>{voucher[field]}%</>;
  } else if (field === 'code') {
    return <div className='flex flex-row justify-left'>
      <span className='hidden md:block md:flex-shrink-0' >
      </span>
      <div className='flex ml-4 inline-block m-auto align-middle'>{voucher.code}</div>
    </div>;
  }
  return <>{voucher[field]}</>;
};

type VoucherEditorRowProps = {
  voucher: Voucher,
  onChangeVoucherValidity: (valid: boolean) => Promise<void>,
  onDeleteVoucher: () => Promise<void>
}

const VoucherEditorRow = ({ voucher, onChangeVoucherValidity, onDeleteVoucher }: VoucherEditorRowProps) => {
  const history = useHistory();
  const [deleteVoucherModalOpen, setDeleteVoucherModalOpen] = useState(false);
  const [confirmDeleteVoucher, setConfirmDeleteVoucher] = useState('');
  const [waitingOnValidityChange, setWaitingOnValidityChange] = useState(false);
  const [deletingVoucher, setDeletingVoucher] = useState(false);

  const onConfirmDeleteVoucher = () => {
    if (confirmDeleteVoucher !== voucher.code) return;
    setDeletingVoucher(true);
    // onDeleteVoucher(), if successful, will trigger a state change that results in this component being no longer
    // mounted - we don't need to set `deletingVoucher`  to false if it's successful (and it will cause an error).
    onDeleteVoucher();
    setDeleteVoucherModalOpen(false);
  };

  const onSetValidity = (valid: boolean) => {
    setWaitingOnValidityChange(true);
    onChangeVoucherValidity(valid).then(() => setWaitingOnValidityChange(false));
  };

  return <React.Fragment>
    <tr>
      {voucherFields.map(field => <td key={`${voucher.code}-${field}`}><VoucherFieldCell
        voucher={voucher}
        field={field as keyof Voucher} /></td>)}
      <td>
        <div className='flex justify-center my-auto'>
          <IconButton
            data-locator='edit-button'
            label={`Edit ${voucher.code}`}
            onClick={() => history.push(`/admin/voucher-editor/${voucher.code}`)}
            src={editIcon}
            alt='edit' />
          {waitingOnValidityChange && <IconLoader />}
          {!waitingOnValidityChange && voucher.isValid && <IconButton
            data-locator='hide-button'
            label={`Hide ${voucher.code}`}
            onClick={() => onSetValidity(false)}
            src={eyeOffIcon}
            alt='hide' />}
          {!waitingOnValidityChange && !voucher.isValid && <IconButton
            data-locator='make-visible-button'
            label={`Make ${voucher.code} valid`}
            onClick={() => onSetValidity(true)}
            src={eyeIcon}
            alt='make valid' />}
          {!deletingVoucher && <IconButton
            data-locator='delete-button'
            label={`Delete ${voucher.code}`}
            onClick={() => setDeleteVoucherModalOpen(true)}
            src={trashIcon}
            alt='delete' />}
          {deletingVoucher && <IconLoader />}
        </div>
      </td>
    </tr>

    <ConfirmationModal
      isOpen={deleteVoucherModalOpen}
      heading={`Permanently Delete "${voucher.code}"`}
      confirmButtonText='Yes, delete'
      onConfirm={onConfirmDeleteVoucher}
      onCancel={() => setDeleteVoucherModalOpen(false)}
    >
      <p>
        <>Deleting this voucher will remove it permanently from the database.</>
        <>Are you sure you don&apos;t want to make it invalid instead?</>
      </p>

      <div className='flex justify-center mt-8 space-x-2'>
        <label htmlFor='confirm_delete_via_code_field' className='my-auto text-lg'>
          Enter the voucher code to delete:
        </label>
        <TextField
          id='confirm_delete_via_code_field'
          value={confirmDeleteVoucher}
          onChange={event => setConfirmDeleteVoucher(event.target.value)}
          validateInput={value => value === voucher.code} />
        <img src={confirmDeleteVoucher === voucher.code
          ? checkCircleIcon
          : alertCircleIcon} />
      </div>
    </ConfirmationModal>
  </React.Fragment>;
};

type ValidityFilterState = null | 'all' | 'valid' | 'invalid';
type DiscountTypeFilterState = null | 'all' | 'percentage' | 'fixed';
interface VoucherAdminOptionsProps {
  currentValidityFilter: ValidityFilterState,
  onValidityFilterChange: (filterState: ValidityFilterState) => void,
  discountTypeFilter: DiscountTypeFilterState,
  onDiscountTypeFilterChange: (filterState: DiscountTypeFilterState) => void,
  voucherSearchFilter: string | null,
  setVoucherSearchFilter: (searchTerm: string) => void,
}

const VoucherAdminOptions = ({
  currentValidityFilter,
  onValidityFilterChange,
  discountTypeFilter,
  onDiscountTypeFilterChange,
  voucherSearchFilter,
  setVoucherSearchFilter,
}: VoucherAdminOptionsProps) => {
  const validityOptions: { value: ValidityFilterState, label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'valid', label: 'Valid' },
    { value: 'invalid', label: 'Invalid' }];
  const discountTypeOptions: { value: DiscountTypeFilterState, label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'percentage', label: 'Percentage' },
    { value: 'fixed', label: 'Fixed' }];

  return <span className='flex flex-row font-semibold'>
    <FilterDropdown
      name='Validity'
      options={validityOptions}
      currentState={currentValidityFilter}
      onChange={(filterState) => onValidityFilterChange(filterState)} />
    <FilterDropdown
      name='Type'
      options={discountTypeOptions}
      currentState={discountTypeFilter}
      onChange={(filterState) => onDiscountTypeFilterChange(filterState)} />
    <div className='grid place-content-end'>
      <SearchField
        label='Search administration table for vouchers'
        className='pl-1 pb-2'
        searchTerm={voucherSearchFilter}
        setSearchTerm={setVoucherSearchFilter}
        onSubmit={() => null} />
    </div>
    <div className='mt-auto mb-2 ml-auto'>
      <Button
        className='font-bold'
        id='create-voucher-button'
        primary
        linkTo='/admin/voucher-creator'
        fieldProps={{}}>Create Voucher</Button>
    </div>
  </span>;
};

const VoucherAdmin = () => {
  const [vouchers, setVouchers] = useState<undefined | Voucher[]>();
  const [validityFilter, setValidityFilter] = useState<ValidityFilterState>(null);
  const [discountTypeFilter, setDiscountTypeFilter] = useState<DiscountTypeFilterState>(null);
  const [voucherSearchFilter, setVoucherSearchFilter] = useState<string | null>(null);

  const getFilteredVouchers = () => {
    if (!vouchers) return vouchers;
    let filteredVouchers = vouchers;
    filteredVouchers = (!validityFilter || validityFilter === 'all')
      ? filteredVouchers
      : filteredVouchers?.filter(
        v => (v.isValid && validityFilter === 'valid') || (!v.isValid && validityFilter === 'invalid'),
      );
    filteredVouchers = (!discountTypeFilter || discountTypeFilter === 'all')
      ? filteredVouchers
      : filteredVouchers?.filter(
        v => (v.discountType === 'Percentage' && discountTypeFilter === 'percentage') || (v.discountType === 'Fixed' && discountTypeFilter === 'fixed'),
      );
    filteredVouchers = voucherSearchFilter
      ? searchWithinVouchers(filteredVouchers, parseSearchTerms(voucherSearchFilter).searchTerms)
      : filteredVouchers;
    return filteredVouchers;
  };

  const onChangeVoucherValidity = (voucher: Voucher, validity: boolean) => {
    return requests.voucher.setValidity(voucher, validity).then(() => {
      setVouchers(
        produce(vouchers, draft => {
          if (!vouchers || !draft) return;
          const target: Voucher | undefined = draft.find(v => v.code === voucher.code);
          if (target) target.isValid = validity;
        }),
      );
    });
  };

  const onDeleteVoucher = (voucher: Voucher) => {
    return requests.voucher.delete(voucher.code).then(() => {
      setVouchers(
        produce(vouchers, draft => {
          const voucherIndex = vouchers?.findIndex(v => v.code === voucher.code);
          if (draft && voucherIndex !== undefined && voucherIndex != -1) {
            draft.splice(voucherIndex, 1);
          } else {
            console.warn('did not update voucher cache');
          }
        }),
      );
    });
  };

  useEffect(() => {
    requests.voucher.getAll()
      .then(v => {
        setVouchers(v);
      });
  }, []);

  return (
    <React.Fragment>
      {!vouchers && <ReactLoading className='m-auto' type={'bubbles'} color={'#ac542e'} height={'50%'} width={'8%'} />}
      {vouchers && <>
        <VoucherAdminOptions
          currentValidityFilter={validityFilter}
          onValidityFilterChange={setValidityFilter}
          discountTypeFilter={discountTypeFilter}
          onDiscountTypeFilterChange={setDiscountTypeFilter}
          voucherSearchFilter={voucherSearchFilter}
          setVoucherSearchFilter={setVoucherSearchFilter} />
        <table className='divide-y divide-gray-200 w-full mt-4 mb-8'>
          <thead className='bg-gray-50 font-extrabold'>
            <tr>
              {voucherFields.map(field => <ColumnHeader key={`header-${field}`} name={field} />)}
              <ColumnHeader name='Actions' />
            </tr>
          </thead>
          <tbody>
            {getFilteredVouchers()?.map(v => <VoucherEditorRow
              key={v.code}
              voucher={v}
              onChangeVoucherValidity={validity => onChangeVoucherValidity(v, validity)}
              onDeleteVoucher={() => onDeleteVoucher(v)}
            />)}
          </tbody>
        </table>
      </>}
    </React.Fragment>
  );
};

export default VoucherAdmin;
